abstract class Repository {}

class BaseRepository extends Repository {
  map<TData>(response: Either<Failure, Response<TData>>): Result<TData, Failure> {
    return response.fold(
      (failure) => {
        if (failure instanceof UnauthorizedException) {
          return Result.failure(failure);
        }
        if (failure instanceof ForbiddenException) {
          return Result.failure(failure);
        }
        return Result.failure(failure);
      },
      (success) => new Result<TData, Failure>({
        fromCache: false,
        hashInfo: this.generateHash(response.toString()),
        isEmpty: success.isNull,
        success: success.data as TData,
        failure: null,
        data: null,
      })
    );
  }

  unauthorized(): UnauthorizedException {
    return new UnauthorizedException();
  }

  private generateHash(input: string): string {
    // Simulação de hash MD5 - em produção usar biblioteca crypto
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  }
}

// Capturar dados da requisição,
// que ocasionou a falha,
// para ajudar na solução do problema.
class RequestData {
  constructor(options: {
    endpoint?: string;
    headers?: Record<string, unknown>;
    queryParams?: Record<string, unknown>;
    payload?: Record<string, unknown>;
    response?: Response<unknown>;
  } = {}) {
    this.endpoint = options.endpoint;
    this.headers = options.headers;
    this.queryParams = options.queryParams;
    this.payload = options.payload;
    this.response = options.response;
  }

  public readonly endpoint?: string;
  public readonly headers?: Record<string, unknown>;
  public readonly queryParams?: Record<string, unknown>;
  public readonly payload?: Record<string, unknown>;
  public readonly response?: Response<unknown>;
}

function checkIsEmpty<TSuccess>(value?: TSuccess): boolean {
  if (value && typeof value === 'object' && 'body' in value) {
    const response = value as unknown as Response<unknown>;
    if (Array.isArray(response.body)) {
      return response.body.length === 0;
    }
    if (typeof response.body === 'object' && response.body !== null) {
      return Object.keys(response.body).length === 0;
    }
    return response.body == null;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object' && value !== null) {
    return Object.keys(value).length === 0;
  }
  return value == null;
}

class Result<TSuccess, TFailure extends Failure> {
  public readonly success?: TSuccess;
  public readonly failure?: TFailure;
  public readonly data?: RequestData;
  public readonly hashInfo: string;
  public readonly fromCache: boolean;
  public readonly isEmpty: boolean;

  constructor(options: {
    fromCache: boolean;
    hashInfo: string;
    isEmpty: boolean;
    success?: TSuccess;
    failure?: TFailure;
    data?: RequestData;
  }) {
    this.fromCache = options.fromCache;
    this.hashInfo = options.hashInfo;
    this.isEmpty = options.isEmpty;
    this.success = options.success;
    this.failure = options.failure;
    this.data = options.data;
  }

  get isSuccess(): boolean {
    return this.failure == null;
  }

  static success<TSuccess>(value?: TSuccess): Result<TSuccess, never> {
    return new Result<TSuccess, never>({
      fromCache: false,
      isEmpty: checkIsEmpty(value),
      hashInfo: this.generateHash(value?.toString() ?? ''),
      success: value,
      failure: undefined as never,
      data: undefined,
    });
  }

  static failure<TFailure extends Failure>(value: TFailure, options?: { data?: RequestData }): Result<never, TFailure> {
    return new Result<never, TFailure>({
      fromCache: false,
      isEmpty: true,
      hashInfo: '',
      failure: value,
      data: options?.data,
    });
  }

  copyWith(options: {
    fromCache?: boolean;
    hashInfo?: string;
    success?: TSuccess;
    failure?: TFailure;
    data?: RequestData;
  }): Result<TSuccess, TFailure> {
    let isEmpty = true;
    const data = options.data ?? this.data;
    
    if (options.success !== undefined) {
      isEmpty = checkIsEmpty(options.success);
      return new Result<TSuccess, TFailure>({
        fromCache: options.fromCache ?? this.fromCache,
        hashInfo: options.hashInfo ?? this.hashInfo,
        isEmpty: isEmpty,
        success: options.success,
        failure: undefined,
        data: data,
      });
    }
    
    if (options.failure !== undefined) {
      return new Result<TSuccess, TFailure>({
        fromCache: options.fromCache ?? this.fromCache,
        hashInfo: options.hashInfo ?? this.hashInfo,
        isEmpty: true,
        success: undefined,
        failure: options.failure,
        data: data,
      });
    }

    return new Result<TSuccess, TFailure>({
      fromCache: options.fromCache ?? this.fromCache,
      hashInfo: options.hashInfo ?? this.hashInfo,
      isEmpty: isEmpty,
      success: this.success,
      failure: this.failure,
      data: data,
    });
  }

  private static generateHash(input: string): string {
    // Simulação de hash MD5 - em produção usar biblioteca crypto
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  }
}

class Failure implements Error {
  private readonly _messages: string[] = [];

  constructor(options: { messages?: string[] } = {}) {
    if (options.messages) {
      for (let i = 0; i < options.messages.length; i++) {
        const message = options.messages[i].replace(/\\n/g, '\n').replace(/"/g, '');
        this._messages.push(message);
      }
    }
  }
    name: string;
    stack?: string;

  get messages(): string[] {
    return [...this._messages];
  }

  get message(): string {
    return this._messages.join(', ');
  }

  toString(): string {
    return 'failure';
  }
}

// Classes de exceção
class UnauthorizedException extends Failure {
  constructor() {
    super({ messages: ['Não autorizado'] });
  }
}

class ForbiddenException extends Failure {
  constructor() {
    super({ messages: ['Acesso negado'] });
  }
}

// Interfaces para simular Either do fpdart
interface Either<L, R> {
  fold<T>(leftFn: (left: L) => T, rightFn: (right: R) => T): T;
}

class Left<L, R> implements Either<L, R> {
  constructor(private readonly value: L) {}

  fold<T>(leftFn: (left: L) => T): T {
    return leftFn(this.value);
  }
}

class Right<L, R> implements Either<L, R> {
  constructor(private readonly value: R) {}

  fold<T>(leftFn: (left: L) => T, rightFn: (right: R) => T): T {
    return rightFn(this.value);
  }
}

// Interface para Response
interface Response<T> {
  data: T;
  isNull: boolean;
  body?: T;
}

export {
  Repository,
  BaseRepository,
  RequestData,
  Result,
  Failure,
  UnauthorizedException,
  ForbiddenException,
  Left,
  Right,
  type Response
};
